import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition, Tab } from '@headlessui/react';
import { XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import { chatsAPI } from '../services/api';

interface IndividualResponsesModalProps {
  isOpen: boolean;
  onClose: () => void;
  chatId: number;
  turnId: number;
}

interface EngineResponse {
  id: number;
  engine_name: string;
  model_name: string;
  round: number;
  response_text: string;
  response_time_ms: number;
  input_tokens: number;
  output_tokens: number;
  cost: number;
  status: string;
  error_message: string | null;
}

export default function IndividualResponsesModal({
  isOpen,
  onClose,
  chatId,
  turnId,
}: IndividualResponsesModalProps) {
  const [responses, setResponses] = useState<EngineResponse[]>([]);
  const [userQuery, setUserQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [isQueryExpanded, setIsQueryExpanded] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsQueryExpanded(false);  // Reset expansion state when modal opens
      loadResponses();
    }
  }, [isOpen, chatId, turnId]);

  const loadResponses = async () => {
    setLoading(true);
    try {
      const response = await chatsAPI.getTurnResponses(chatId, turnId);
      setResponses(response.data.responses);
      setUserQuery(response.data.user_query);
    } catch (error) {
      console.error('Failed to load responses:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group responses by engine
  const engineGroups = responses.reduce((acc, resp) => {
    const key = resp.engine_name;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(resp);
    return acc;
  }, {} as Record<string, EngineResponse[]>);

  const engines = Object.keys(engineGroups);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl max-h-[80vh] transform overflow-hidden rounded-2xl bg-surface-800 border border-surface-700 shadow-xl transition-all flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-surface-700">
                  <Dialog.Title className="text-lg font-semibold text-surface-100">
                    Individual Responses
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="p-1 rounded-lg hover:bg-surface-700 transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5 text-surface-400" />
                  </button>
                </div>

                {/* Query */}
                <div className="p-4 bg-surface-700/50 border-b border-surface-700">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-medium text-surface-400">
                      Original Query
                    </h4>
                    {userQuery.length > 150 && (
                      <button
                        onClick={() => setIsQueryExpanded(!isQueryExpanded)}
                        className="text-xs text-primary-400 hover:text-primary-300 transition-colors flex items-center gap-1"
                      >
                        {isQueryExpanded ? (
                          <>
                            <ChevronUpIcon className="w-3 h-3" />
                            Collapse
                          </>
                        ) : (
                          <>
                            <ChevronDownIcon className="w-3 h-3" />
                            Expand
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  <p
                    className={`text-surface-200 ${
                      !isQueryExpanded && userQuery.length > 150 ? 'line-clamp-3' : ''
                    }`}
                  >
                    {userQuery}
                  </p>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden">
                  {loading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
                    </div>
                  ) : (
                    <Tab.Group>
                      <Tab.List className="flex gap-1 p-2 bg-surface-700/50 border-b border-surface-700 overflow-x-auto">
                        {engines.map((engine) => (
                          <Tab
                            key={engine}
                            className={({ selected }) =>
                              `px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                                selected
                                  ? 'bg-primary-600 text-white'
                                  : 'text-surface-400 hover:bg-surface-600'
                              }`
                            }
                          >
                            {engine}
                          </Tab>
                        ))}
                      </Tab.List>

                      <Tab.Panels className="overflow-y-auto max-h-[50vh]">
                        {engines.map((engine) => (
                          <Tab.Panel key={engine} className="p-4">
                            {engineGroups[engine].map((resp) => (
                              <div
                                key={resp.id}
                                className="mb-6 last:mb-0"
                              >
                                <div className="flex items-center gap-4 mb-2">
                                  <h4 className="font-medium text-surface-200">
                                    Round {resp.round}
                                  </h4>
                                  <span className="text-sm text-surface-400">
                                    {resp.model_name}
                                  </span>
                                  <span className="text-sm text-surface-500">
                                    {(resp.response_time_ms / 1000).toFixed(1)}s
                                  </span>
                                  <span className="text-sm text-surface-500">
                                    ${resp.cost.toFixed(4)}
                                  </span>
                                  <span
                                    className={`text-sm ${
                                      resp.status === 'success'
                                        ? 'text-green-400'
                                        : 'text-red-400'
                                    }`}
                                  >
                                    {resp.status}
                                  </span>
                                </div>

                                {resp.status === 'success' ? (
                                  <div className="prose max-w-none bg-surface-700/50 rounded-lg p-4">
                                    <ReactMarkdown>
                                      {resp.response_text}
                                    </ReactMarkdown>
                                  </div>
                                ) : (
                                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
                                    {resp.error_message || 'Unknown error'}
                                  </div>
                                )}
                              </div>
                            ))}
                          </Tab.Panel>
                        ))}
                      </Tab.Panels>
                    </Tab.Group>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
